import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const MAX_SCALE = 4;
const MIN_SCALE = 1;

const ImageLightbox = ({ images = [], open, startIndex = 0, onClose }) => {
  const [active, setActive] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const touchStartX = useRef(null);
  const dragState = useRef(null);
  const pinchState = useRef(null);

  const close = useCallback(() => onClose && onClose(), [onClose]);
  const resetZoom = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, []);
  const prev = useCallback(() => { resetZoom(); setActive((i) => (i - 1 + images.length) % images.length); }, [images.length, resetZoom]);
  const next = useCallback(() => { resetZoom(); setActive((i) => (i + 1) % images.length); }, [images.length, resetZoom]);

  useEffect(() => { if (open) { setActive(startIndex); resetZoom(); } }, [open, startIndex, resetZoom]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close, prev, next]);

  const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

  const onWheel = (e) => {
    e.stopPropagation();
    const delta = -e.deltaY * 0.0015;
    setScale((s) => {
      const ns = clampScale(s + delta * s);
      if (ns === 1) setOffset({ x: 0, y: 0 });
      return ns;
    });
  };

  const onDoubleClick = (e) => {
    e.stopPropagation();
    if (scale > 1) resetZoom();
    else setScale(2.5);
  };

  // pointer / touch panning + pinch
  const onPointerDown = (e) => {
    e.stopPropagation();
    if (scale > 1) {
      dragState.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
    }
  };
  const onPointerMove = (e) => {
    if (!dragState.current || scale <= 1) return;
    e.stopPropagation();
    setOffset({
      x: dragState.current.ox + (e.clientX - dragState.current.startX),
      y: dragState.current.oy + (e.clientY - dragState.current.startY),
    });
  };
  const endPointer = () => { dragState.current = null; };

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchState.current = { d: dist(e.touches), s: scale };
    } else if (scale > 1 && e.touches.length === 1) {
      dragState.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, ox: offset.x, oy: offset.y };
    } else {
      touchStartX.current = e.touches[0].clientX;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchState.current) {
      const ns = clampScale(pinchState.current.s * (dist(e.touches) / pinchState.current.d));
      setScale(ns);
      if (ns === 1) setOffset({ x: 0, y: 0 });
    } else if (dragState.current && scale > 1 && e.touches.length === 1) {
      setOffset({
        x: dragState.current.ox + (e.touches[0].clientX - dragState.current.startX),
        y: dragState.current.oy + (e.touches[0].clientY - dragState.current.startY),
      });
    }
  };
  const onTouchEnd = (e) => {
    pinchState.current = null;
    dragState.current = null;
    if (scale === 1 && touchStartX.current !== null && e.changedTouches.length) {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(dx) > 50 && images.length > 1) (dx < 0 ? next() : prev());
    }
    touchStartX.current = null;
  };

  return createPortal(
    <AnimatePresence>
      {open && images.length > 0 && (
        <motion.div
          className="fixed inset-0 z-[100] bg-[#0b0b0b]/95 backdrop-blur-sm flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={(e) => { e.stopPropagation(); if (scale === 1) close(); }}
          data-testid="image-lightbox"
        >
          <button onClick={(e) => { e.stopPropagation(); close(); }} data-testid="image-lightbox-close"
            className="absolute top-5 right-5 z-10 text-[#e7d3a1] hover:text-white p-2">
            <X size={30} />
          </button>

          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="image-lightbox-prev"
                className="absolute left-3 sm:left-8 z-10 text-[#e7d3a1] hover:text-white p-2 bg-[#0b0b0b]/40 rounded-full">
                <ChevronLeft size={34} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="image-lightbox-next"
                className="absolute right-3 sm:right-8 z-10 text-[#e7d3a1] hover:text-white p-2 bg-[#0b0b0b]/40 rounded-full">
                <ChevronRight size={34} />
              </button>
            </>
          )}

          <div
            className="flex items-center justify-center"
            style={{ cursor: scale > 1 ? 'grab' : 'zoom-in', touchAction: 'none' }}
            onWheel={onWheel}
            onDoubleClick={onDoubleClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerLeave={endPointer}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img
              key={active}
              src={images[active]}
              alt="Enlarged product"
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
              data-testid="image-lightbox-img"
              style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transition: dragState.current ? 'none' : 'transform 0.18s ease-out' }}
              className="max-h-[86vh] max-w-[92vw] object-contain shadow-2xl select-none"
            />
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 overline text-[0.6rem] text-[#9c9587]">
            {images.length > 1 && <span>{active + 1} / {images.length}</span>}
            <span className="flex items-center gap-1.5"><ZoomIn size={13} /> Scroll / double-tap to zoom</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ImageLightbox;
